
import { useState, useCallback, useEffect } from 'react';
import { ExamResult, ExamType, ExamStats, ExamFilter } from '@/utils/exam/types';
import { 
  getStudentExams, 
  addExamResult, 
  updateExamResult, 
  deleteExamResult,
  getExamStatistics
} from '@/services/exam';

export const useStudentExams = (studentId: number | string) => {
  const [examResults, setExamResults] = useState<ExamResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<ExamResult[]>([]);
  const [stats, setStats] = useState<ExamStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ExamFilter>({});

  // Fetch all exam results for a student
  const fetchExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const results = await getStudentExams(studentId);
      // Convert all results to ensure types match
      const typedResults: ExamResult[] = results.map(result => ({
        ...result,
        createdAt: typeof result.createdAt === 'object' 
          ? result.createdAt.toISOString() 
          : result.createdAt
      }));
      
      setExamResults(typedResults);
      
      // Also update filtered results based on current filters
      applyFilters(typedResults, filters);
      
      // Get statistics
      const examStats = await getExamStatistics(studentId);
      setStats(examStats);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching exams:', err);
      setError('Failed to load exam results');
      setLoading(false);
    }
  }, [studentId, filters]);

  // Apply filters to exam results
  const applyFilters = useCallback((results: ExamResult[], currentFilters: ExamFilter) => {
    let filtered = [...results];
    
    if (currentFilters.examType) {
      filtered = filtered.filter(exam => exam.examType === currentFilters.examType);
    }
    
    if (currentFilters.subject) {
      const subjectLower = currentFilters.subject.toLowerCase();
      filtered = filtered.filter(exam => 
        exam.subject.toLowerCase().includes(subjectLower)
      );
    }
    
    if (currentFilters.programTitle) {
      const programLower = currentFilters.programTitle.toLowerCase();
      filtered = filtered.filter(exam => 
        exam.programTitle?.toLowerCase().includes(programLower)
      );
    }
    
    if (currentFilters.dateFrom) {
      const fromDate = new Date(currentFilters.dateFrom);
      filtered = filtered.filter(exam => {
        const examDate = new Date(exam.examDate);
        return examDate >= fromDate;
      });
    }
    
    if (currentFilters.dateTo) {
      const toDate = new Date(currentFilters.dateTo);
      filtered = filtered.filter(exam => {
        const examDate = new Date(exam.examDate);
        return examDate <= toDate;
      });
    }
    
    setFilteredResults(filtered);
  }, []);

  // Add a new exam result
  const addExam = useCallback(async (examData: Omit<ExamResult, 'id' | 'createdAt'>) => {
    const success = await addExamResult(studentId, examData);
    if (success) {
      await fetchExams();
    }
    return success;
  }, [studentId, fetchExams]);

  // Update an existing exam result
  const updateExam = useCallback(async (
    examId: string, 
    examData: Partial<Omit<ExamResult, 'id' | 'createdAt'>>
  ) => {
    const success = await updateExamResult(studentId, examId, examData);
    if (success) {
      await fetchExams();
    }
    return success;
  }, [studentId, fetchExams]);

  // Delete an exam result
  const deleteExam = useCallback(async (examId: string) => {
    const success = await deleteExamResult(studentId, examId);
    if (success) {
      await fetchExams();
    }
    return success;
  }, [studentId, fetchExams]);

  // Update filters
  const updateFilters = useCallback((newFilters: ExamFilter) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      applyFilters(examResults, updated);
      return updated;
    });
  }, [examResults, applyFilters]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setFilteredResults(examResults);
  }, [examResults]);

  // Initial data fetch
  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  return {
    examResults,
    filteredResults,
    stats,
    loading,
    error,
    filters,
    fetchExams,
    addExam,
    updateExam,
    deleteExam,
    updateFilters,
    clearFilters
  };
};
